import { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';

import { errorToast, warningToast } from './Toasty';

interface CsvDropzoneProps {
    // Called with the file's raw text contents and its name once a CSV is dropped/selected.
    onFileLoaded: (text: string, fileName: string) => void;
}

/**
 * A single-file drop target for CSV uploads. Reads the dropped file as text and hands the
 * raw contents to the parent; it does not parse or validate the CSV itself.
 */
const CsvDropzone = ({ onFileLoaded }: CsvDropzoneProps) => {
    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onabort = () => warningToast(<div>File reading was aborted!</div>, true);
        reader.onerror = () => errorToast(<div>File reading has failed!</div>);
        reader.onload = (e: ProgressEvent<FileReader>) => {
            const text = (e.target?.result as string) ?? '';
            onFileLoaded(text, file.name);
        };
        reader.readAsText(file);
    }, [onFileLoaded]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        maxFiles: 1,
        accept: { 'text/csv': ['.csv'] },
    });

    return (
        <div {...getRootProps()} className={`csv-dropzone${isDragActive ? ' csv-dropzone-active' : ''}`}>
            <input {...getInputProps()} />
            <p className='mb-0'>Drag &amp; drop the CSV export here, or click to choose a file</p>
        </div>
    );
};

export default CsvDropzone;
